import { DataSource } from '@angular/cdk/collections';

import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, delay } from 'rxjs/operators';

import { Model, ModelKey, ModelKeyOf } from './model';
import { ModelResource } from './model-resource';
import { ObjectQueryParameters, ObjectQuerySortDirection, ObjectQueryFilterOperator } from './object-query-parameters';

/**
 * A list filter: its operator and value. The value is whatever the filter
 * component set (a text, an id or a list of ids), so code reading it narrows
 * it first; the query parameters send it as text.
 */
export interface ListFilter {
  operator: string;
  value: unknown;
}

export class ListState {
  pager: {
    page: number,
    size: number
  };
  sort: {
    key: string,
    direction: ObjectQuerySortDirection
  };
  filters: {
    [name: string]: ListFilter
  };
}

export class ObjectList<T extends Model<K>, K extends ModelKey = ModelKeyOf<T>> implements DataSource<T> {

  public loadingObservable: Observable<boolean>;
  total = 0;
  count = 0;
  private listName: string;
  private resource: ModelResource<T, K>;
  private loadingSubject: BehaviorSubject<boolean>;
  private dataSubject = new Subject<T[]>();
  private querySubscription: Subscription;

  private pager$: BehaviorSubject<{ page: number, size: number }>;
  private sort$: BehaviorSubject<{ key: string, direction: string }>;
  public filters$: BehaviorSubject<{ [name: string]: ListFilter }>;

  get state(): ListState {
    return {
      pager: this.pager$.getValue(),
      sort: {
        key: this.sort$.getValue().key,
        direction: this.sort$.getValue().direction as ObjectQuerySortDirection,
      },
      filters: this.filters$.getValue()
    };
  }

  constructor(listName: string,
    resource: ModelResource<T, K>) {
    this.listName = listName;
    this.resource = resource;

    this.pager$ = new BehaviorSubject({ page: 1, size: 20 });
    this.sort$ = new BehaviorSubject({ key: 'created', direction: 'desc' });
    // AI: Initialize with a default 'text' filter. This ensures the filter state
    // is valid from the moment of creation, preventing race conditions where UI
    // components could read an 'undefined' property and display it.
    this.filters$ = new BehaviorSubject({
      text: { operator: ObjectQueryFilterOperator.Contains, value: '' }
    });

    this.loadingSubject = new BehaviorSubject<boolean>(false);
    this.loadingObservable = this.loadingSubject.asObservable();
  }

  setPage(page: number, size: number) {
    this.pager$.next({ page, size });
  }

  setSort(key: string, direction: string) {
    this.sort$.next({ key, direction });
    this.setPage(1, this.state.pager.size);
  }

  public setFilter(name: string, value: unknown, operator?: string) {
    const currentFilters = { ...this.filters$.getValue() };
    const existingFilter = currentFilters[name];

    // AI: This logic ensures a filter object always exists and always has a valid operator.
    // This prevents race conditions in UI components that might otherwise read an undefined property.
    if (existingFilter) {
      // AI: An existing filter is being updated.
      existingFilter.value = value;
      // AI: Only update the operator if a new one is explicitly provided.
      if (operator) {
        existingFilter.operator = operator;
      }
    } else {
      // AI: A new filter is being created.
      currentFilters[name] = {
        value: value,
        // AI: Default to 'Contains' if no operator is specified.
        operator: operator || ObjectQueryFilterOperator.Contains
      };
    }

    this.filters$.next(currentFilters);
    this.setPage(1, this.state.pager.size);
  }

  public connect(): Observable<T[]> {
    this.querySubscription = combineLatest([this.pager$, this.sort$, this.filters$]).pipe(
      debounceTime(50))
      .subscribe(() => {
        this.loadingSubject.next(true);
        this.resource.queryPaged(this.createObjectQueryParameters())
          .pipe(delay(0))
          .subscribe((res) => {
            this.total = res.total;
            this.count = res.elements.length;
            this.loadingSubject.next(false);
            this.dataSubject.next(res.elements);
          });
      });

    return this.dataSubject.asObservable();
  }

  public disconnect(): void {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
    this.dataSubject.complete();
    this.loadingSubject.complete();
    this.pager$.complete();
    this.sort$.complete();
    this.filters$.complete();
  }

  createObjectQueryParameters(): ObjectQueryParameters {
    const params = new ObjectQueryParameters();
    const currentState = this.state;
    params.setPager(currentState.pager.page, currentState.pager.size);
    params.addSort(currentState.sort.key, currentState.sort.direction as ObjectQuerySortDirection);
    for (const name in currentState.filters) {
      if (Object.prototype.hasOwnProperty.call(currentState.filters, name)) {
        const filter = currentState.filters[name];
        if (filter.value !== null && filter.value !== '' && filter.value !== undefined) {
          params.addFilter(name, filter.operator as ObjectQueryFilterOperator, String(filter.value));
        }
      }
    }
    return params;
  }
}
